import { useCallback, useEffect, useState } from "react";
import { getAuditLogs } from "../../../services/audit/audit.service";
import { AuditLogEntry } from "../../../services/audit/audit.types";
import { getTrash } from "../../../services/trash/trash.service";
import { TrashEntity } from "../../../services/trash/trash.types";
import { getHealthSummary } from "../../../services/content-health/content-health.service";
import {
  ContentHealthSummaryResponse,
  ProgramHealthSummary,
} from "../../../services/content-health/content-health.types";
import {
  getFullPlatformStatus,
  FullPlatformStatus,
} from "../../../services/platform-status";

// Single data source for the Dashboard page: the KPI row and the detail cards
// share these states so each backend source is fetched exactly once per load.

export const RECENT_ACTIVITY_LIMIT = 8;
export const TRASH_DAYS_WINDOW = 7;

export const TRASH_ENTITIES: { entity: TrashEntity; label: string }[] = [
  { entity: "program", label: "Programas" },
  { entity: "course", label: "Cursos" },
  { entity: "lesson", label: "Lecciones" },
  { entity: "resource", label: "Recursos" },
  { entity: "project", label: "Proyectos" },
];

export interface ActivityState {
  logs: AuditLogEntry[];
  // Total reported by the audit-logs endpoint (null until loaded/failed).
  total: number | null;
  loading: boolean;
  error: Error | null;
}

export interface TrashState {
  // Per-entity totals; a rejected entity keeps null (rendered as "—").
  totals: Record<TrashEntity, number | null> | null;
  // Aggregated total across the fulfilled entities (null until loaded/failed).
  total: number | null;
  loading: boolean;
  error: Error | null;
}

export interface HealthState {
  summary: ContentHealthSummaryResponse | null;
  loading: boolean;
  refreshing: boolean;
  error: Error | null;
}

export interface PlatformStatusState {
  status: FullPlatformStatus | null;
  loading: boolean;
  error: Error | null;
}

/** Total unhealthy items across programs — the "Incidencias de salud" KPI
 * and the health card's attention-queue link share this count. */
export const sumHealthIncidents = (programs: ProgramHealthSummary[]): number =>
  programs.reduce(
    (sum, program) =>
      sum +
      program.deleted +
      program.detached +
      program.broken +
      program.incomplete,
    0
  );

export const useDashboardData = () => {
  const [activity, setActivity] = useState<ActivityState>({
    logs: [],
    total: null,
    loading: false,
    error: null,
  });
  const [trash, setTrash] = useState<TrashState>({
    totals: null,
    total: null,
    loading: false,
    error: null,
  });
  const [health, setHealth] = useState<HealthState>({
    summary: null,
    loading: false,
    refreshing: false,
    error: null,
  });
  const [platform, setPlatform] = useState<PlatformStatusState>({
    status: null,
    loading: false,
    error: null,
  });

  const fetchActivity = useCallback(async () => {
    setActivity((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await getAuditLogs({
        page: 1,
        limit: RECENT_ACTIVITY_LIMIT,
      });
      setActivity({
        logs: response.data,
        total: response.total ?? response.data.length,
        loading: false,
        error: null,
      });
    } catch (err) {
      setActivity({
        logs: [],
        total: null,
        loading: false,
        error: err as Error,
      });
    }
  }, []);

  const fetchTrash = useCallback(async () => {
    setTrash((prev) => ({ ...prev, loading: true, error: null }));
    // allSettled: one failing entity must not hide the other four counts.
    // Failed entities render as "—"; only a full wipeout shows the error state.
    const results = await Promise.allSettled(
      TRASH_ENTITIES.map(({ entity }) =>
        getTrash(entity, { page: 1, limit: 1, days: TRASH_DAYS_WINDOW })
      )
    );
    if (results.every((result) => result.status === "rejected")) {
      setTrash({
        totals: null,
        total: null,
        loading: false,
        error: new Error("trash summary unavailable"),
      });
      return;
    }
    const totals = {} as Record<TrashEntity, number | null>;
    TRASH_ENTITIES.forEach(({ entity }, index) => {
      const result = results[index];
      totals[entity] =
        result.status === "fulfilled" ? result.value.total : null;
    });
    const total = Object.values(totals).reduce<number>(
      (sum, count) => sum + (count ?? 0),
      0
    );
    setTrash({ totals, total, loading: false, error: null });
  }, []);

  const fetchHealth = useCallback(async (refresh?: boolean) => {
    setHealth((prev) => ({
      ...prev,
      loading: !refresh,
      refreshing: Boolean(refresh),
      error: null,
    }));
    try {
      const summary = await getHealthSummary(refresh);
      setHealth({ summary, loading: false, refreshing: false, error: null });
    } catch (err) {
      setHealth((prev) => ({
        ...prev,
        loading: false,
        refreshing: false,
        error: err as Error,
      }));
    }
  }, []);

  const fetchPlatformStatus = useCallback(async () => {
    setPlatform((prev) => ({ ...prev, loading: true, error: null }));
    try {
      // Admin read: /platform-status/full also returns allowed_school_ids
      const status = await getFullPlatformStatus();
      setPlatform({ status, loading: false, error: null });
    } catch (err) {
      setPlatform({ status: null, loading: false, error: err as Error });
    }
  }, []);

  // One fetch per source per page load; the KPI row and cards share the data.
  useEffect(() => {
    fetchActivity();
    fetchTrash();
    fetchHealth();
    fetchPlatformStatus();
  }, [fetchActivity, fetchTrash, fetchHealth, fetchPlatformStatus]);

  return {
    activity,
    trash,
    health,
    platform,
    refetchActivity: fetchActivity,
    refetchTrash: fetchTrash,
    refetchHealth: fetchHealth,
    refetchPlatformStatus: fetchPlatformStatus,
  };
};
